import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '../../common/functions/cn';

const alertVariants = cva(
  [
    'group/alert relative grid w-full gap-0.5 rounded-md border px-2.5 py-2 text-left text-sm',
    'has-data-[slot=alert-action]:relative has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2 has-data-[slot=alert-action]:pr-18',
    "*:[svg:not([class*='size-'])]:size-4 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current",
  ],
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        destructive: 'bg-card text-error *:data-[slot=alert-description]:text-error/90 *:[svg]:text-current',
        primary:
          'border-primary/40 bg-accent text-card-foreground *:data-[slot=alert-description]:text-card-foreground *:[svg]:text-accent-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Alert = ({ className, variant, ...props }: ComponentProps<'div'> & VariantProps<typeof alertVariants>) => {
  return <div data-slot="alert" role="alert" className={cn(alertVariants({ variant }), className)} {...props} />;
};

const AlertTitle = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'font-semibold group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground',
        className
      )}
      {...props}
    />
  );
};

const AlertDescription = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-balance text-muted-foreground text-sm md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4',
        className
      )}
      {...props}
    />
  );
};

const AlertAction = ({ className, ...props }: ComponentProps<'div'>) => {
  return <div data-slot="alert-action" className={cn('absolute top-2 right-2', className)} {...props} />;
};

export { Alert, AlertAction, AlertDescription, AlertTitle };
